import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import FastScanInput from "@/components/FastScanInput";
import { INV_STATUS, INV_ORDER, INV_TIPOS, CATEGORIAS, fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Loader2, Plus, ClipboardCheck, Play } from "lucide-react";
import { toast } from "sonner";

function InvBadge({ status }) {
  const s = INV_STATUS[status] || { label: status, cls: "text-slate-300 bg-slate-800 border-slate-600" };
  return <span className={`inline-flex rounded-sm border px-2.5 py-1 text-xs font-bold uppercase font-mono ${s.cls}`} data-testid={`inv-status-${status}`}>{s.label}</span>;
}

function InvTimeline({ current }) {
  if (current === "CANCELADO") return <div className="text-red-300 font-mono text-sm border border-red-600 rounded px-3 py-2 bg-red-950/50">INVENTÁRIO CANCELADO</div>;
  const idx = INV_ORDER.indexOf(current);
  return (
    <div className="flex gap-2">
      {INV_ORDER.map((st, i) => {
        const done = i < idx, active = i === idx;
        return (
          <div key={st} className="flex flex-col items-center min-w-[100px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold ${active ? "border-primary bg-primary text-primary-foreground" : done ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : "border-border bg-card text-muted-foreground"}`}>{done ? <Check className="w-4 h-4" /> : i + 1}</div>
            <div className={`text-[9px] mt-1 text-center uppercase font-mono ${active ? "text-primary" : done ? "text-emerald-300" : "text-muted-foreground"}`}>{INV_STATUS[st].label}</div>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_FILTERS = [
  { id: "", label: "Todos" },
  { id: "ABERTO", label: "Abertos" },
  { id: "EM_CONTAGEM", label: "Em Contagem" },
  { id: "CONFERIDO", label: "Conferidos" },
  { id: "FINALIZADO", label: "Finalizados" },
];

export default function Inventarios() {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ tipo: "GERAL", unit_id: "", categoria: "" });
  const [ajustes, setAjustes] = useState({});
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");

  useEffect(() => { setStatusFilter(searchParams.get("status") || ""); }, [searchParams]);

  const load = useCallback(() => api.get("/inventarios").then((r) => setList(r.data)), []);
  const loadInv = async (id) => { const { data } = await api.get(`/inventarios/${id}`); setSel(data); };
  useEffect(() => {
    load();
    api.get("/units").then((r) => { setUnits(r.data); if (r.data.length) setForm((f) => ({ ...f, unit_id: r.data[0].id })); });
  }, [load]);

  const flashScreen = (ok) => { setFlash(ok ? "scan-flash-ok" : "scan-flash-err"); setTimeout(() => setFlash(""), 500); };
  const act = async (fn) => { setBusy(true); try { await fn(); await loadInv(sel.id); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); } };

  const criar = async () => {
    if (!form.unit_id) return toast.error("Selecione a unidade");
    setBusy(true);
    try {
      const payload = { tipo: form.tipo, unit_id: form.unit_id };
      if (form.tipo === "CATEGORIA" && form.categoria) payload.categoria = form.categoria;
      const { data } = await api.post("/inventarios", payload);
      toast.success(`Inventário ${data.numero} criado`);
      setCreateOpen(false); await load(); loadInv(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar"); }
    finally { setBusy(false); }
  };

  const bipar = async ({ codigo, quantidade, is_master }) => {
    try {
      const { data } = await api.post(`/inventarios/${sel.id}/contar/bipar`, { codigo, quantidade, is_master });
      flashScreen(true);
      toast.success(`${data.nome}: contado ${data.contado} (dif ${data.diferenca})`);
      await loadInv(sel.id);
    } catch (e) { flashScreen(false); toast.error(e.response?.data?.detail || "Bipagem inválida"); }
  };

  const setManual = async (sku_id, contado) => {
    try { await api.post(`/inventarios/${sel.id}/contar/manual`, { sku_id, contado: Number(contado) || 0 }); await loadInv(sel.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const abrirFinalizar = () => { const a = {}; sel.itens.forEach((it) => { a[it.id] = it.diferenca !== 0; }); setAjustes(a); };
  const finalizar = async () => { await act(async () => { await api.post(`/inventarios/${sel.id}/finalizar`, { ajustes }); }); toast.success("Inventário finalizado — ajustes aplicados"); };

  if (sel) {
    const st = sel.status;
    const divergentes = sel.itens.filter((it) => it.diferenca !== 0);
    return (
      <div className={`space-y-5 ${flash} rounded-lg`} data-testid="inventario-detalhe">
        <Button variant="outline" size="sm" onClick={() => setSel(null)}>← Inventários</Button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">{sel.numero}</h1>
            <div className="text-sm text-muted-foreground">{INV_TIPOS.find((t) => t.id === sel.tipo)?.label} · {sel.itens.length} itens · {fmtDate(sel.created_at)}</div>
          </div>
          <InvBadge status={st} />
        </div>

        <Card className="bg-card/70 p-4"><InvTimeline current={st} /></Card>

        <Card className="bg-card/70 p-4">
          <div className="flex flex-wrap gap-2 items-center">
            {st === "ABERTO" && <Button onClick={() => act(() => api.post(`/inventarios/${sel.id}/iniciar`))} disabled={busy} data-testid="btn-iniciar-inv"><Play className="w-4 h-4" /> Iniciar Contagem</Button>}
            {st === "EM_CONTAGEM" && <Button onClick={() => act(() => api.post(`/inventarios/${sel.id}/conferir`))} disabled={busy} data-testid="btn-conferir-inv"><ClipboardCheck className="w-4 h-4" /> Conferir Contagem</Button>}
            {st === "CONFERIDO" && <Button onClick={() => { abrirFinalizar(); }} disabled={busy} data-testid="btn-preparar-finalizar" variant="secondary">Revisar ajustes abaixo</Button>}
            {["EM_CONTAGEM", "CONFERIDO"].includes(st) && <span className="text-sm text-muted-foreground">Divergências: <b className="text-amber-300 font-mono">{divergentes.length}</b></span>}
            {st === "FINALIZADO" && <div className="text-emerald-300 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Finalizado — {sel.ajustes_aplicados || 0} ajuste(s) aplicado(s)</div>}
          </div>
        </Card>

        {st === "EM_CONTAGEM" && <FastScanInput label="Bipar Contagem" onScan={bipar} testid="inv-scan" />}

        <Card className="bg-card/70 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-3">Produto</th>
                <th className="text-right p-3">Sistema</th>
                <th className="text-right p-3">Contado</th>
                <th className="text-right p-3">Diferença</th>
                {st === "CONFERIDO" && <th className="text-center p-3">Aplicar Ajuste</th>}
              </tr>
            </thead>
            <tbody>
              {sel.itens.map((it) => (
                <tr key={it.id} className="border-b border-border/60" data-testid={`inv-item-${it.sku}`}>
                  <td className="p-3"><div className="font-medium">{it.nome}</div><div className="text-[10px] font-mono text-muted-foreground">{it.sku} · {it.categoria}</div></td>
                  <td className="p-3 text-right font-mono">{it.saldo_sistema}</td>
                  <td className="p-3 text-right font-mono">
                    {st === "EM_CONTAGEM" ? (
                      <Input type="number" defaultValue={it.contado} onBlur={(e) => setManual(it.sku_id, e.target.value)} className="w-20 h-8 text-right font-mono ml-auto" data-testid={`inv-manual-${it.sku}`} />
                    ) : it.contado}
                  </td>
                  <td className={`p-3 text-right font-mono font-bold ${it.diferenca === 0 ? "text-muted-foreground" : it.diferenca > 0 ? "text-emerald-300" : "text-red-300"}`}>{st === "ABERTO" ? "—" : `${it.diferenca > 0 ? "+" : ""}${it.diferenca}`}</td>
                  {st === "CONFERIDO" && (
                    <td className="p-3 text-center">
                      {it.diferenca !== 0 ? <Switch checked={!!ajustes[it.id]} onCheckedChange={(v) => setAjustes((a) => ({ ...a, [it.id]: v }))} data-testid={`inv-ajuste-${it.sku}`} /> : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {st === "CONFERIDO" && (
          <Button className="w-full h-14 text-lg font-bold" onClick={finalizar} disabled={busy} data-testid="btn-finalizar-inv">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aprovar Ajustes e Finalizar Inventário"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="inventarios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Acuracidade</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><ClipboardCheck className="w-8 h-8 text-primary" /> Inventário</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-novo-inventario"><Plus className="w-4 h-4" /> Novo Inventário</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <Button key={s.id} variant={statusFilter === s.id ? "default" : "outline"} size="sm" onClick={() => setStatusFilter(s.id)} data-testid={`inv-filtro-${s.id || "todos"}`}>{s.label}</Button>
        ))}
      </div>

      {!list ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (() => { const listaFiltrada = statusFilter ? list.filter((d) => d.status === statusFilter) : list; return listaFiltrada.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhum inventário {statusFilter ? `com status ${INV_STATUS[statusFilter]?.label || statusFilter}` : "registrado"}.</Card>
      ) : (
        <div className="grid gap-3">
          {listaFiltrada.map((d) => {
            const divs = d.itens.filter((it) => it.diferenca !== 0).length;
            return (
              <Card key={d.id} className="bg-card/70 p-4 flex items-center justify-between cursor-pointer hover:border-primary/50" onClick={() => loadInv(d.id)} data-testid={`inv-card-${d.numero}`}>
                <div>
                  <div className="font-mono font-bold text-lg">{d.numero}</div>
                  <div className="text-xs text-muted-foreground">{INV_TIPOS.find((t) => t.id === d.tipo)?.label} · {d.itens.length} itens · {divs} divergência(s)</div>
                </div>
                <InvBadge status={d.status} />
              </Card>
            );
          })}
        </div>
      ); })()}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Novo Inventário</DialogTitle>
            <DialogDescription>Escolha o tipo e o local. O saldo do sistema é fotografado na criação.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="inv-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{INV_TIPOS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unidade / Local</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}>
                <SelectTrigger data-testid="inv-unidade"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.tipo === "CATEGORIA" && (
              <div>
                <Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm((f) => ({ ...f, categoria: v }))}>
                  <SelectTrigger data-testid="inv-categoria"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="text-xs text-muted-foreground bg-secondary/40 rounded p-2">Cíclico usa uma amostra de itens; Geral/Unidade abrange todos; Categoria filtra pela categoria escolhida.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={criar} disabled={busy} data-testid="btn-criar-inventario">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Inventário"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
